import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DataComponentFilterOffer from './DataComponent__FilterOffer';
import DataComponentDisplayControl from './DataComponent__DisplayControl';
import DateComponentSettings from './DateComponent_Settings';
import DateComponentCurrencySettings from './DateComponent_CurrencySettings';
import Pagination from '../Pagination.js';
import query from '../../helpers/query';
import DataComponentBodyOffer from "./DataComponent__BodyOffer";

const DataComponentOffer = ({
    isQuery = false,
    loading = false,
    page,
    alignPagination = false,
    settings = false,
    fields = [],
    className = '',
    settingKey = '',
    columnData = {
        header: {},
        body: {}
    },
    onAction,
    hideResetButton,
    gray,
    displayControl,
    headerNames,
    tableBodyTitle,
    currencySettings,
    currencySettingKey,
    currencySettingData,
    multiLevelHeaderNames,
    totalPages,
    data,
    t
}) => {
    const [state, setState] = useState({
        filters: {},
        typeDisplayControl: 'table',
        page: page || 1
    });

    const onActionHandler = useCallback((type, nextFilters = null) => {
        const stateData = nextFilters ? { ...nextFilters } : { ...state.filters };

        if (onAction) {
            onAction(stateData, type);
        }

        if (isQuery) {
            query.replace(stateData);
        }
    }, [state.filters, onAction, isQuery]);

    const onPageChange = useCallback((page) => {
        const nextFilters = {
            ...state.filters,
            page
        };

        setState(prevState => ({
            ...prevState,
            filters: {
                ...nextFilters
            }
        }));

        onActionHandler('pageChange', nextFilters);
    }, [onActionHandler, state.filters]);

    const onFiltersApply = useCallback((stateData, type = 'filtersChange') => {
        setState(prevState => ({
            ...prevState,
            filters: {
                ...stateData
            }
        }));

        onActionHandler(type, stateData);
    }, [onActionHandler]);

    const onDisplayControlChange = useCallback((type) => {
        setState(prevState => ({
            ...prevState,
            typeDisplayControl: type
        }));
    }, []);

    const getClassFooter = useCallback(() => {
        return classNames({
            'ibox-footer': true,
            'text-right': alignPagination
        });
    }, [alignPagination]);

    useEffect(() => {
        if (page !== state.page) {
            setState(prevState => ({
                ...prevState,
                page: page || prevState.page
            }));
        }
    }, [page]);

    useEffect(() => {
        if (!fields.length) {
            onActionHandler('firstRender');
        }
    }, [fields.length, onActionHandler]);

    return (
        <div className="ibox">
            {fields.length
                ? (
                    <DataComponentFilterOffer
                        fields={fields}
                        loading={loading}
                        onAction={onFiltersApply}
                        hideResetButton={hideResetButton}
                        gray={gray}
                    />
                )
                : null
            }
            <DateComponentSettings 
                settings={settings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={settingKey}
                loading={loading}
                data={data}
                filters={state.filters}
            />
            <DateComponentCurrencySettings
                settings={currencySettings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={currencySettingKey}
                loading={loading}
                data={currencySettingData}
                filters={state.filters}
            />
            <div className="ibox-content">
                {displayControl
                    ? <DataComponentDisplayControl
                        data={displayControl.data}
                        type={state.typeDisplayControl}
                        name={displayControl.name}
                        onChange={onDisplayControlChange}
                      />
                    : null
                }
                <DataComponentBodyOffer
                    data={data}
                    headerNames={headerNames}
                    multiLevelHeaderNames={multiLevelHeaderNames}
                    columnData={columnData}
                    loading={loading}
                    settings={settings}
                    settingKey={settingKey}
                    displayControl={{
                        ...displayControl,
                        type: state.typeDisplayControl
                    }}
                    filters={state.filters}
                    className={className}
                />
            </div>
            {totalPages > 1
                ? <div className={getClassFooter()}>
                    <Pagination
                        onPageChange={onPageChange}
                        initialPage={state.filters.page}
                        totalPages={totalPages}
                        loading={loading}
                        align={alignPagination ? 'right' : 'left'}
                    />
                  </div>
                : null
            }
        </div>
    );
};

DataComponentOffer.propTypes = {
    isQuery: PropTypes.bool,
    loading: PropTypes.bool,
    page: PropTypes.number,
    alignPagination: PropTypes.bool,
    settings: PropTypes.bool,
    fields: PropTypes.array,
    className: PropTypes.string,
    settingKey: PropTypes.string,
    columnData: PropTypes.shape({
        header: PropTypes.object,
        body: PropTypes.object
    }),
    onAction: PropTypes.func,
    hideResetButton: PropTypes.bool,
    gray: PropTypes.bool,
    displayControl: PropTypes.shape({
        type: PropTypes.string,
        name: PropTypes.string,
        data: PropTypes.array
    }),
    headerNames: PropTypes.array,
    tableBodyTitle: PropTypes.string,
    currencySettings: PropTypes.bool,
    currencySettingKey: PropTypes.string,
    currencySettingData: PropTypes.array,
    multiLevelHeaderNames: PropTypes.array,
    totalPages: PropTypes.number,
    data: PropTypes.array,
    t: PropTypes.object
};

DataComponentOffer.defaultProps = {
    isQuery: false,
    loading: false,
    page: 1,
    alignPagination: false,
    settings: false,
    fields: [],
    className: '',
    settingKey: '',
    columnData: {
        header: {},
        body: {}
    },
    onAction: () => {},
    hideResetButton: false,
    gray: false,
    displayControl: null,
    headerNames: [],
    tableBodyTitle: '',
    currencySettings: false,
    currencySettingKey: '',
    currencySettingData: [],
    multiLevelHeaderNames: [],
    totalPages: 0,
    data: [],
    t: {}
};

export default withTranslation(DataComponentOffer);